import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { InnerHero } from '@/components/sections'
import { Accordion } from '@/components/ui'
import type { AccordionItem } from '@/components/ui'
import { Prefooter, Footer } from '@/components/layout'

function getFaqItems(t: (k: string) => string): AccordionItem[] {
  return [
    {
      id: 'faq-nichel',
      question: t('faq.q1'),
      answer: t('faq.a1'),
    },
    {
      id: 'faq-icea',
      question: t('faq.q2'),
      answer: t('faq.a2'),
    },
    {
      id: 'faq-animali',
      question: t('faq.q3'),
      answer: t('faq.a3'),
    },
    {
      id: 'faq-siliconi',
      question: t('faq.q4'),
      answer: t('faq.a4'),
    },
    {
      id: 'faq-biologica',
      question: t('faq.q5'),
      answer: t('faq.a5'),
    },
  ]
}

export function FAQPage() {
  const { t } = useTranslation('common')
  const faqItems = getFaqItems(t)

  return (
    <main>
      <InnerHero
        eyebrow={t('faq.heroEyebrow')}
        title={t('faq.heroTitle')}
        titleItalic={t('faq.heroTitleItalic')}
        body={t('faq.heroBody')}
        breadcrumbs={[
          { label: t('nav.home'), href: '/it' },
          { label: t('nav.faq') },
        ]}
      />

      {/* FAQ accordion */}
      <section
        className="relative bg-cream py-20 md:py-28 overflow-hidden"
        aria-label={t('faq.sectionAriaLabel')}
      >
        <div className="nashi-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            {/* Left rail sticky meta */}
            <motion.aside
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 1 }}
              className="lg:col-span-4"
            >
              <div className="lg:sticky lg:top-32">
                <span className="nashi-eyebrow">{t('faq.indexLabel')}</span>
                <p className="mt-6 font-display italic text-argan-deep text-2xl md:text-3xl leading-snug">
                  {String(faqItems.length).padStart(2, '0')} {t('faq.questionsCount')}
                </p>
                <p className="mt-6 max-w-xs text-bruno-soft leading-relaxed text-sm md:text-base">
                  {t('faq.contactPrompt')}
                </p>
                <a
                  href="/it/contattaci"
                  className="group mt-8 inline-flex items-center gap-3 text-[11px] font-medium uppercase tracking-[0.24em] text-bruno pb-2 border-b border-bruno/40 hover:border-argan-deep hover:text-argan-deep transition-colors"
                >
                  {t('faq.contactLink')}
                  <span className="inline-block transition-transform duration-500 group-hover:translate-x-1.5">
                    &rarr;
                  </span>
                </a>
              </div>
            </motion.aside>

            {/* Right accordion */}
            <div className="lg:col-span-8">
              <Accordion items={faqItems} defaultOpen={0} />
            </div>
          </div>
        </div>
      </section>

      {/* Edge band closing call to dialog */}
      <section className="relative bg-cream-soft py-20 md:py-28 overflow-hidden">
        <div className="nashi-container">
          <div className="max-w-3xl mx-auto text-center">
            <motion.span
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.9 }}
              className="block font-display italic text-argan-deep text-3xl mb-6"
            >
              &#10022;
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 1, delay: 0.1 }}
              className="font-display text-bruno"
              style={{
                fontSize: 'clamp(1.875rem, 3.5vw, 2.75rem)',
                lineHeight: 1.05,
                letterSpacing: '-0.015em',
              }}
            >
              <span className="block">{t('faq.ctaTitle1')}</span>
              <span className="block italic font-light text-argan-deep">{t('faq.ctaTitle2')}</span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.9, delay: 0.2 }}
              className="mt-6 text-bruno-soft text-base md:text-lg leading-relaxed max-w-md mx-auto"
            >
              {t('faq.ctaBody')}
            </motion.p>
            <motion.a
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.9, delay: 0.3 }}
              href="/it/contattaci"
              className="mt-10 inline-flex items-center gap-3 bg-bruno text-cream font-medium uppercase tracking-[0.28em] text-[11px] px-10 py-4 hover:bg-argan-deep transition-colors"
            >
              {t('faq.contactLink')}
              <span aria-hidden="true">&rarr;</span>
            </motion.a>
          </div>
        </div>
      </section>

      <Prefooter />
      <Footer />
    </main>
  )
}
